/**
 * Auth callback — OAuth sign-in and password recovery.
 *
 * ## Password reset was broken two ways (2026-09-17)
 *
 * Margo Dixon (LXUS6Y) requested three resets in seven minutes and was told
 * the link was expired every time. Across the userbase, 557 recovery links
 * had been requested, 485 were spent, and only **73 password changes had ever
 * completed**. Two independent causes, both fixed here:
 *
 *   1. **The PKCE recovery path was dead code.** `if (type === 'recovery')`
 *      returned unconditionally when there was no fragment session, so the
 *      branch below it — commented "including PKCE password recovery" — could
 *      never run. A reset arriving as `?code=...&type=recovery`, which is
 *      exactly what the current Supabase email template produces, was never
 *      exchanged; every customer was told the link was invalid and bounced to
 *      Home three seconds later. That branch is now gated on actually having a
 *      fragment token, so the code exchange is reachable.
 *
 *   2. **Scanners spend the token before the customer does.** A Supabase
 *      recovery token dies on first touch, and email security scanners
 *      (Outlook/Defender Safe Links and friends) follow links automatically on
 *      delivery: 259 of those 557 links were consumed within 15 seconds of
 *      being sent, Margo's three at 34, 36 and 61 seconds. So the token is now
 *      spent on a **click**, not on page load — a scanner that renders this
 *      page, even one executing JavaScript, does not press a button.
 *
 * Failures are also classified rather than echoed. She was shown Supabase's
 * own "Email link is invalid or has expired", which reads as "wait for a
 * fresher email", so she requested another and looped. The copy now says the
 * link was already used and offers a new one, and a recovery failure no longer
 * bounces to Home, because a customer cannot act on a message they never got
 * to read.
 *
 * Still outstanding, and not fixable from here: the Supabase "Reset password"
 * template should link at `{{ .RedirectTo }}?token_hash={{ .TokenHash }}&type=recovery`
 * so the link points at this page instead of Supabase's `/auth/v1/verify`,
 * which any plain GET can burn. See docs/PASSWORD_RESET_SETUP.md. Until then
 * recovery arrives on the `code` path and a pre-spent token still fails — with
 * honest copy now instead of a bounce.
 *
 * OAuth is deliberately left automatic: those codes are never emailed, so
 * there is no scanner to beat and an extra click would only add friction.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { landingUrlFor } from '@/constants/navigation';
import { isNewlyCreatedUser, trackSignupConversion } from '@/lib/chatgptAdsPixel';
import { describeRecoveryLinkError } from '@/lib/authErrors';

/** Where a customer goes to ask for a fresh link (opens the form directly). */
const FORGOT_PASSWORD_URL = '/home?auth_mode=forgot';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = React.useState(null);
  /** Classified recovery-link failure: rendered until the customer acts on it. */
  const [linkError, setLinkError] = React.useState(null);
  /** A recovery token held un-spent until the customer presses the button. */
  const [pendingRecovery, setPendingRecovery] = React.useState(null);
  const [isVerifying, setIsVerifying] = React.useState(false);

  React.useEffect(() => {
    /** Google and Apple hand sign-in and sign-up back through this same
     *  callback with the same shape, so "did someone just create an account?"
     *  is decided by how old the account is — see isNewlyCreatedUser. Password
     *  signups report themselves from Layout's own success path. */
    const trackOAuthSignup = (user) => {
      if (user && isNewlyCreatedUser(user)) trackSignupConversion(user);
    };

    const handleCallback = async () => {
      try {
        const { supabase } = await import('../lib/supabase');

        // Check both URL search params and hash params
        const params = new URLSearchParams(location.search);
        const hashParams = new URLSearchParams(location.hash.substring(1));

        const code = params.get('code');
        const type = params.get('type') || hashParams.get('type');
        const tokenHash = params.get('token_hash');
        // Supabase reports a dead recovery token by redirecting here with
        // these — in the query on the PKCE flow, in the hash on the implicit
        // one. Reading only the query missed the implicit case entirely and
        // fell through to "Invalid OAuth response".
        const errorParam = params.get('error') || hashParams.get('error');
        const errorCode = params.get('error_code') || hashParams.get('error_code');
        const errorDescription = params.get('error_description') || hashParams.get('error_description');

        if (errorParam) {
          console.error('Auth callback error param:', { errorParam, errorCode, errorDescription });
          // A dead recovery link is a recovery problem, not a failed sign-in.
          const looksLikeRecovery = type === 'recovery'
            || Boolean(tokenHash)
            || /otp|expired|access_denied/i.test(`${errorCode} ${errorDescription}`);
          if (looksLikeRecovery) {
            setLinkError(describeRecoveryLinkError({ errorCode, errorDescription }));
            return;
          }
          setError(errorDescription || errorParam);
          setTimeout(() => navigate(createPageUrl('Home')), 3000);
          return;
        }

        // Password recovery via token_hash (works cross-device, no PKCE).
        // Hold the token — do NOT verify here. See the header: verifying on
        // mount is what let scanners spend it first.
        if (tokenHash && type === 'recovery') {
          setPendingRecovery({ tokenHash });
          return;
        }

        // Legacy/implicit recovery: the session is already in the fragment,
        // which never reaches a server and so cannot be scanned — nothing to
        // hold back here.
        //
        // This is gated on actually HAVING a token. It used to be `if (type ===
        // 'recovery')` with an unconditional `return`, which made the PKCE
        // recovery branch below ("including PKCE password recovery")
        // unreachable dead code — so a reset arriving as
        // `?code=...&type=recovery`, which is exactly what the current Supabase
        // email template produces, could never be exchanged. It always fell
        // into the else and told the customer the link was invalid. That is
        // why 485 recovery tokens were spent and only 73 passwords were ever
        // set (2026-09-17).
        const implicitAccessToken = hashParams.get('access_token') || params.get('access_token');
        if (type === 'recovery' && implicitAccessToken) {
          const refreshToken = hashParams.get('refresh_token') || params.get('refresh_token');
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: implicitAccessToken,
            refresh_token: refreshToken || ''
          });

          if (sessionError) {
            console.error('Recovery session error:', sessionError);
            setLinkError(describeRecoveryLinkError(sessionError));
            return;
          }

          // Successfully established recovery session - redirect to reset password page
          navigate('/reset-password', { replace: true });
          return;
        }

        // Handle OAuth or PKCE code exchange (including PKCE password recovery)
        if (code) {
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('Session exchange error:', exchangeError);
            // On a recovery code this is the common real-world failure while
            // the email template still points at Supabase's verify endpoint:
            // the token was already burned, or the PKCE verifier lives in a
            // different browser than the one the mail app opened.
            if (type === 'recovery') {
              setLinkError(describeRecoveryLinkError(exchangeError));
              return;
            }
            setError('Failed to complete sign in. Please try again.');
            setTimeout(() => navigate(createPageUrl('Home')), 3000);
            return;
          }

          if (data.session) {
            if (type === 'recovery') {
              navigate('/reset-password', { replace: true });
              return;
            }
            trackOAuthSignup(data.session.user);
            // Check for saved redirect path from AuthGate
            const redirectPath = sessionStorage.getItem('auth_redirect');
            if (redirectPath) {
              sessionStorage.removeItem('auth_redirect');
              navigate(redirectPath);
            } else {
              // No specific destination in mind — admins open on the inquiry
              // queue, everyone else on Home. Checked AFTER auth_redirect so a
              // deep link the user was actually trying to reach still wins.
              navigate(landingUrlFor(data.session.user));
            }
          } else if (type === 'recovery') {
            setLinkError(describeRecoveryLinkError({ errorCode: 'otp_expired' }));
          } else {
            setError('No session created. Please try again.');
            setTimeout(() => navigate(createPageUrl('Home')), 3000);
          }
        } else {
          // Handle direct token in hash (for OAuth providers)
          const accessToken = hashParams.get('access_token');

          if (accessToken) {
            const { data: setData, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || ''
            });

            if (sessionError) {
              console.error('Session set error:', sessionError);
              setError('Failed to complete sign in. Please try again.');
              setTimeout(() => navigate(createPageUrl('Home')), 3000);
              return;
            }

            trackOAuthSignup(setData?.session?.user);

            // Check for saved redirect path from AuthGate
            const redirectPath = sessionStorage.getItem('auth_redirect');
            if (redirectPath) {
              sessionStorage.removeItem('auth_redirect');
              navigate(redirectPath);
            } else {
              navigate(landingUrlFor(setData?.session?.user));
            }
          } else if (type === 'recovery') {
            // A recovery callback carrying nothing usable: no token_hash, no
            // code, no fragment session. Treat it as a spent link and offer a
            // fresh one rather than calling it an OAuth failure.
            setLinkError(describeRecoveryLinkError({ errorCode: 'otp_expired' }));
          } else {
            setError('Invalid OAuth response. Please try again.');
            setTimeout(() => navigate(createPageUrl('Home')), 3000);
          }
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setError('An error occurred during sign in. Please try again.');
        setTimeout(() => navigate(createPageUrl('Home')), 3000);
      }
    };

    handleCallback();
  }, [navigate, location]);

  /** Spends the held recovery token. Only ever called from a real click. */
  const confirmRecovery = async () => {
    if (!pendingRecovery || isVerifying) return;
    setIsVerifying(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: pendingRecovery.tokenHash,
        type: 'recovery'
      });
      if (verifyError) {
        console.error('Recovery verifyOtp error:', verifyError);
        setPendingRecovery(null);
        setLinkError(describeRecoveryLinkError(verifyError));
        return;
      }
      if (data?.session) {
        navigate('/reset-password', { replace: true });
        return;
      }
      setPendingRecovery(null);
      setLinkError(describeRecoveryLinkError({ errorCode: 'otp_expired' }));
    } catch (err) {
      console.error('Recovery verify threw:', err);
      setPendingRecovery(null);
      setLinkError(describeRecoveryLinkError(err));
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-[70vh] w-full flex items-center justify-center bg-black">
      <div className="text-center">
        <img
          src="/defera-logo-web.png"
          alt="Defera Logo"
          className={`h-12 mx-auto mb-8 ${linkError || pendingRecovery ? '' : 'animate-pulse'}`}
        />

        {linkError ? (
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 max-w-md mx-auto text-left">
            <p className="text-white text-base mb-2">{linkError.title}</p>
            <p className="text-sm text-white/70 font-normal">{linkError.detail}</p>
            {linkError.canRequestNew && (
              <button
                type="button"
                onClick={() => navigate(FORGOT_PASSWORD_URL)}
                className="mt-5 w-full h-11 rounded-full bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Send me a new link
              </button>
            )}
          </div>
        ) : pendingRecovery ? (
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 max-w-md mx-auto text-left">
            <p className="text-white text-base mb-2">Reset your password</p>
            <p className="text-sm text-white/70 font-normal">
              Press continue and we&rsquo;ll take you straight to a new password.
            </p>
            <button
              type="button"
              onClick={confirmRecovery}
              disabled={isVerifying}
              className="mt-5 w-full h-11 rounded-full bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-60"
            >
              {isVerifying ? 'One moment…' : 'Continue'}
            </button>
          </div>
        ) : error ? (
          <div className="text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-4 max-w-md mx-auto">
            <p className="font-medium mb-2">Sign In Failed</p>
            <p className="text-sm">{error}</p>
            <p className="text-xs mt-3 text-gray-400">Redirecting to home page...</p>
          </div>
        ) : (
          <p className="text-gray-400">Completing sign in...</p>
        )}
      </div>
    </div>
  );
}
